import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import apiPaths from 'constants/api-paths';
import { appDb } from 'dexie/app.db';
import { generateURL } from 'helpers/api-path.helper';
import {
  switchMap,
  from,
  Observable,
  map,
  catchError,
  throwError,
  of,
} from 'rxjs';
import { CacheKeyService } from '../cache-key.service';
import { DBService } from '../db.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorTypesApiService {
  protected http = inject(HttpClient);
  protected dbService = inject(DBService);
  protected cacheKeyService = inject(CacheKeyService);

  getList(cacheKey?: string) {
    return this.http
      .get(
        generateURL(
          apiPaths.typeErrorList,
          {},
          new HttpParams({ fromObject: { cacheKey } })
        )
      )
      .pipe(
        switchMap((data: any) => {
          if (data.isAllDb) {
            return from(
              appDb.errorType.clear().then(() =>
                appDb.errorType.bulkAdd(
                  data.items.map((item) => ({
                    ...item,
                    isSynced: 1,
                    isDeleted: item.isDeleted ? 1 : 0,
                  }))
                )
              )
            );
          }
          return from(
            appDb.errorType.bulkPut(
              data.items.map((item) => ({
                ...item,
                isSynced: 1,
                isDeleted: item.isDeleted ? 1 : 0,
              }))
            )
          );
        }),
        catchError(() => {
          return of(null);
        })
      );
  }

  create(data): Observable<{ isOffline: boolean } | { [_: string]: any }> {
    return this.http.post(generateURL(apiPaths.typeError), data).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'typeErrorKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('typeErrorKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  update(id, data) {
    return this.http.put(generateURL(apiPaths.typeErrorId, { id }), data).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'typeErrorKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('typeErrorKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  delete(id) {
    return this.http.delete(generateURL(apiPaths.typeErrorId, { id })).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'typeErrorKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('typeErrorKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }

  bulk(errorTypes) {
    return this.http.post(
      generateURL(apiPaths.typeErrorBulk),
      errorTypes.map((item) => ({
        ...item,
        isDeleted: item.isDeleted ? true : false,
      }))
    );
  }
}
